import { useEffect, useState, type ReactElement } from 'react';
import { useRouter } from 'next/router';
import { useTranslation } from 'next-i18next';
import { components } from 'react-select';
// Components
import { IndexSwitcher } from '../index-switcher/index-switcher';
import { IndexRankingDistribution } from './index-ranking-distribution';
import { DistributionKeyFigures } from './distribution-key-figures';
import { FilterSelect, type FilterSelectOption } from '@utmb/design-system/filter-select/filter-select';
// Contexts
import { useUser } from '@utmb/contexts/AuthContext';
import { useTenancy } from '@utmb/contexts/TenancyContext';
import { useCountry } from '@utmb/contexts/CountryContext';
// Types
import { PiCategoryEnum } from '@utmb/types/enum/PiCategoryEnum';
import { GenderEnum } from '@utmb/types/enum/GenderEnum';
import RunnerStatisticView from '@utmb/types/view/RunnerStatisticView';
import RunnerStatisticIndexDistributionView from '@utmb/types/view/RunnerStatisticIndexDistributionView';
import { ColorTypesEnum } from '@utmb/types/colors';
// Utils
import { fetchTheAPI } from '@utmb/utils/utmb';
import { getColorStyle } from '@utmb/utils/colors';
import { VALID_INDEX_THRESHOLD } from '@utmb/utils/utmb-index.constants';
// Styles
import styles from './index-distribution.module.scss';
import { Font } from '@utmb/design-system/text/font';

export interface IndexDistributionProps {
    runnerStatistic: RunnerStatisticView | null;
    runnerUri?: string;
    profilePicture: string | null;
    fullname?: string;
}

const CountryOption = (props: any): ReactElement => {
    const { children, data } = props;
    return (
        <components.Option {...props}>
            <div className={data.isContinent ? '' : styles.countryOption}>{children}</div>
        </components.Option>
    );
};

export function IndexDistribution({ runnerStatistic, runnerUri, profilePicture, fullname }: IndexDistributionProps): ReactElement {
    const { t } = useTranslation();
    const { query, locale } = useRouter();
    const { getToken, isAuthenticated } = useUser();
    const { tenantId } = useTenancy();
    const { fullCountriesByContinent, initFullCountriesByContinent } = useCountry();

    const effectiveRunnerUri = runnerUri ?? (query.runner as string | undefined);

    const [selectedCategory, setSelectedCategory] = useState<PiCategoryEnum>(PiCategoryEnum.GENERAL);
    const [statistic, setStatistic] = useState<RunnerStatisticView | null>(runnerStatistic);
    const [filteredDistribution, setFilteredDistribution] = useState<RunnerStatisticIndexDistributionView | null>(null);

    const [gender, setGender] = useState<GenderEnum | null>(null);
    const [ageGroup, setAgeGroup] = useState<string | null>(null);
    const [countryCode, setCountryCode] = useState<string | null>(null);
    const [continentId, setContinentId] = useState<number | null>(null);

    useEffect(() => {
        initFullCountriesByContinent();
    }, []);

    useEffect(() => {
        let cancelled = false;
        async function fetchStat(): Promise<void> {
            const token = await getToken();
            if (!token || !effectiveRunnerUri) return;

            const stat = (await fetchTheAPI({
                tenantId,
                path: `/runners-statistics/${effectiveRunnerUri}`,
                params: { lang: locale as string, category: selectedCategory },
                bearer: token,
            })) as RunnerStatisticView | null;

            if (!cancelled && stat) {
                setStatistic(stat);
                setFilteredDistribution(null);
            }
        }
        if (isAuthenticated) fetchStat();
        return () => {
            cancelled = true;
        };
    }, [selectedCategory, isAuthenticated, effectiveRunnerUri, locale, tenantId, getToken]);

    useEffect(() => {
        if (gender === null) setAgeGroup(null);
    }, [gender]);

    useEffect(() => {
        const hasAnyFilter = gender !== null || ageGroup !== null || countryCode !== null || continentId !== null;
        if (!hasAnyFilter) {
            setFilteredDistribution(null);
            return;
        }
        let cancelled = false;
        async function fetchDist(): Promise<void> {
            const token = await getToken();
            if (!token) return;

            const params: Record<string, string> = { category: selectedCategory };
            if (gender) params.gender = gender;
            if (ageGroup) params.ageGroup = ageGroup;
            if (countryCode) params.countryCode = countryCode;
            if (continentId !== null) params.continentId = String(continentId);
            if (statistic?.index) params.index = String(statistic.index);

            const dist = (await fetchTheAPI({
                tenantId,
                path: `/runners-statistics/distribution`,
                params,
                bearer: token,
                isClient: true,
            })) as RunnerStatisticIndexDistributionView | null;

            if (!cancelled && dist) {
                setFilteredDistribution(dist);
            }
        }
        if (isAuthenticated) fetchDist();
        return () => {
            cancelled = true;
        };
    }, [gender, ageGroup, countryCode, continentId, selectedCategory, statistic?.index, isAuthenticated, tenantId, getToken]);

    const distribution = (filteredDistribution?.distribution ?? statistic?.distribution?.distribution ?? []).filter(
        (interval) => Math.max(interval.indexMin, interval.indexMax) > VALID_INDEX_THRESHOLD
    );
    const displayedRank = filteredDistribution?.rank ?? statistic?.distribution?.rank ?? statistic?.rank ?? null;
    const displayedTotalRanked = filteredDistribution?.totalRanked ?? statistic?.distribution?.totalRanked ?? statistic?.totalRanked ?? 0;
    const displayedTopPercentage = displayedRank && displayedTotalRanked ? Math.round((displayedRank * 100) / displayedTotalRanked) : null;

    const genderOptions: FilterSelectOption<string>[] = [
        { value: GenderEnum.MALE, label: t('common.gender.men') },
        { value: GenderEnum.FEMALE, label: t('common.gender.women') },
    ];

    const ageGroupOptions: FilterSelectOption<string>[] = (statistic?.ageGroups ?? []).map((ag) => ({ value: ag, label: ag }));

    const countryOptions: FilterSelectOption<string | number>[] = fullCountriesByContinent
        .map((continent) => [
            { isContinent: true, value: continent.continentId, label: continent.continentName } as FilterSelectOption<string | number>,
            ...continent.countries.map(
                (country) =>
                    ({
                        isContinent: false,
                        label: country.name,
                        value: country.codeCio,
                    } as FilterSelectOption<string | number>)
            ),
        ])
        .flat();

    return (
        <div className={styles.indexDistribution}>
            <div className={styles.header}>
                <Font.FuturaMedium as="h3" desktop="64" mobile="28" className={styles.title} color={getColorStyle(ColorTypesEnum.WHITE)}>
                    {t('singleRunner.statistics.global.worldRanking')}
                </Font.FuturaMedium>
                <div className={styles.wrapFilters}>
                    <IndexSwitcher value={selectedCategory} onChange={setSelectedCategory} />
                    <div className={styles.wrapSelectFilters}>
                        <FilterSelect<string>
                            placeholder={t('singleRunner.statistics.distribution.gender')}
                            options={genderOptions}
                            value={genderOptions.find((o) => o.value === gender) ?? null}
                            onChange={(opt) => setGender((opt?.value as GenderEnum | undefined) ?? null)}
                        />
                        <FilterSelect<string>
                            placeholder={t('singleRunner.statistics.distribution.ageGroup')}
                            options={ageGroupOptions}
                            value={ageGroupOptions.find((o) => o.value === ageGroup) ?? null}
                            onChange={(opt) => setAgeGroup(opt?.value ?? null)}
                            isDisabled={gender === null}
                        />
                        <FilterSelect<string | number>
                            placeholder={t('singleRunner.statistics.distribution.continentOrCountry')}
                            options={countryOptions}
                            value={countryOptions.find((o) => (continentId !== null ? o.value === continentId : o.value === countryCode)) ?? null}
                            onChange={(opt) => {
                                if (!opt) {
                                    setContinentId(null);
                                    setCountryCode(null);
                                } else if (opt.isContinent) {
                                    setContinentId(opt.value as number);
                                    setCountryCode(null);
                                } else {
                                    setCountryCode(opt.value as string);
                                    setContinentId(null);
                                }
                            }}
                            optionComponent={CountryOption}
                        />
                    </div>
                </div>
            </div>
            <div className={styles.content}>
                <div className={styles.wrapKeyFigures}>
                    <DistributionKeyFigures
                        rank={displayedRank}
                        totalRanked={displayedTotalRanked}
                        topPercentage={displayedTopPercentage}
                        category={selectedCategory}
                    />
                </div>
                <div className={styles.wrapDistribution}>
                    <IndexRankingDistribution
                        distribution={distribution}
                        profilePicture={profilePicture}
                        fullname={fullname}
                        category={selectedCategory}
                        runnerIndex={statistic?.index ?? null}
                    />
                </div>
            </div>
        </div>
    );
}
